import React, { useEffect, useState } from 'react';

/**
 * Typed newsletter composer — pick a newsletter TYPE (launch/nurture,
 * product-update, digest, changelog, educational, event, welcome), give a topic
 * OR paste/upload a changelog, and the engine writes + self-evaluates the draft
 * (POST /api/:brand/newsletter/generate-typed). Mirrors the Social Studio
 * format-picker idea, for newsletters.
 */

interface NewsletterComposerProps { brand: string }
interface TypeOption { id: string; label: string; job: string; framework: string }
interface EvalResult {
  hardFails: string[];
  slopFlags: string[];
  score?: number;
  band?: string;
}
interface ComposeResult {
  type: string;
  subject: string;
  subjectVariants: string[];
  preheader: string;
  body: string;
  ps: string;
  ctaUrl?: string;
  evaluation: EvalResult;
}

const bandColor = (band?: string): string =>
  band === 'ship' ? 'var(--success, #16a34a)'
    : band === 'minor-edits' ? '#0891b2'
    : band === 'revise' ? 'var(--warning, #d97706)'
    : 'var(--danger)';

export default function NewsletterComposer({ brand }: NewsletterComposerProps) {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [type, setType] = useState('launch-nurture');
  const [topic, setTopic] = useState('');
  const [changelog, setChangelog] = useState('');
  const [audience, setAudience] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [result, setResult] = useState<ComposeResult | null>(null);

  useEffect(() => {
    fetch(`/api/${brand}/newsletter/types`)
      .then((r) => r.json())
      .then((d) => setTypes(d.types ?? []))
      .catch(() => { /* picker falls back to the default id */ });
  }, [brand]);

  const selected = types.find((t) => t.id === type);
  const isChangelogType = type === 'changelog' || type === 'product-update';

  function onChangelogFile(file: File) {
    const fr = new FileReader();
    fr.onload = () => setChangelog(String(fr.result ?? ''));
    fr.onerror = () => setMsg('Error: could not read file');
    fr.readAsText(file); // .md / .txt changelogs are plain text
  }

  async function generate() {
    if (!topic.trim() && !changelog.trim()) { setMsg('Add a topic or paste a changelog.'); return; }
    setBusy(true); setMsg('Writing + evaluating…'); setResult(null);
    try {
      const r = await fetch(`/api/${brand}/newsletter/generate-typed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, topic: topic.trim() || undefined, changelog: changelog.trim() || undefined, audience: audience.trim() || undefined }),
      });
      const d = await r.json();
      if (d?.ok && d.result) { setResult(d.result); setMsg(''); }
      else setMsg(`Error: ${String(d?.error ?? 'generation failed').slice(0, 200)}`); // cap so a raw server error can't dump into the UI
    } catch (e) { setMsg(`Error: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  const ev = result?.evaluation;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 0, padding: open ? 16 : '10px 16px', marginBottom: 16, background: 'var(--bg-surface)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, padding: 0, display: 'flex', gap: 8, alignItems: 'center' }}
      >
        {open ? '▾' : '▸'} Compose a newsletter (by type / from changelog)
      </button>

      {open && (
        <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
          <div>
            <label htmlFor="newsletter-type" style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Type</label>
            <select id="newsletter-type" value={type} onChange={(e) => setType(e.target.value)}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }}>
              {(types.length ? types : [{ id: 'launch-nurture', label: 'Launch / Nurture', job: '', framework: '' }]).map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {selected && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{selected.job} · <em>{selected.framework}</em></div>}
          </div>

          <div>
            <label htmlFor="newsletter-topic" style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Topic / brief</label>
            <textarea id="newsletter-topic" value={topic} onChange={(e) => setTopic(e.target.value)} rows={2}
              placeholder="What is this newsletter about? (the angle, the news, the ask)"
              style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
          </div>

          <div>
            <label htmlFor="newsletter-changelog" style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Changelog {isChangelogType ? '' : '(optional)'} — paste or upload (.md/.txt)
            </label>
            <textarea id="newsletter-changelog" value={changelog} onChange={(e) => setChangelog(e.target.value)} rows={4}
              placeholder={"Paste a changelog — e.g.\nAdded: blast-radius check before every apply\nFixed: false-positive idle detection on GPU nodes"}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--mono, monospace)', resize: 'vertical' }} />
            <input type="file" accept=".md,.markdown,.txt" onChange={(e) => { const f = e.target.files?.[0]; if (f) onChangelogFile(f); e.currentTarget.value = ''; }} style={{ fontSize: 12, marginTop: 6 }} />
          </div>

          <div>
            <label htmlFor="newsletter-audience" style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Audience (optional)</label>
            <input id="newsletter-audience" type="text" value={audience} onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. DevOps / SRE, CFO / FinOps, Cloud architect"
              style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-primary" onClick={generate} disabled={busy} style={{ cursor: 'pointer' }}>
              {busy ? 'Working…' : 'Generate + evaluate'}
            </button>
            {msg && <span style={{ fontSize: 12, color: msg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{msg}</span>}
          </div>

          {result && (
            <div style={{ display: 'grid', gap: 10, marginTop: 4, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              {ev && (
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  {ev.band === 'reject' || ev.hardFails.length > 0
                    ? <span style={{ fontWeight: 700, color: 'var(--danger)' }}>REJECTED (hard-fail)</span>
                    : <span style={{ fontWeight: 700, color: bandColor(ev.band) }}>{ev.score != null ? `${ev.score}/100` : '—'} · {ev.band ?? 'scored'}</span>}
                </div>
              )}
              {ev && ev.hardFails.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--danger)' }}>
                  <strong>Hard-fails:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{ev.hardFails.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              {ev && ev.slopFlags.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--warning, #d97706)' }}>
                  <strong>Slop signals:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{ev.slopFlags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              <div><span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>SUBJECT</span><div style={{ fontWeight: 600 }}>{result.subject}</div></div>
              {result.subjectVariants.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Variants: {result.subjectVariants.join(' · ')}</div>
              )}
              {result.preheader && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}><span style={{ color: 'var(--text-tertiary)' }}>PREHEADER</span> {result.preheader}</div>}
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: 'var(--text-primary)', margin: 0 }}>{result.body}</pre>
              {result.ps && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}><strong>P.S.</strong> {result.ps}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
